import {variantClasses} from "./responsive";

/**
 * Semantic accent roles, named the way `TypographyColor` names them — a component says what a colour
 * *means* and the design system decides which token that is, so `success` can never be tone-green in
 * one component and something else in the next. Which token each role maps to lives in the consuming
 * component's class map, because the property differs (Tag fills, Card draws a rail).
 *
 * A const array rather than a bare union because Tag and Card both need the list at runtime, as do
 * their tests, stories and the DesignSystem gallery.
 */
export const ACCENT_COLORS = ["primary", "critical", "medium", "low", "success", "info"] as const;

export type AccentColor = typeof ACCENT_COLORS[number];

/**
 * The v3 palette, as Tailwind names it — see the `colors` block in tailwind.config.js. Raw tokens, not
 * roles: this is for props that paint a literal surface (Stack's backgroundColor), where the caller
 * has already decided which colour it wants.
 */
export type PaletteColor =
	"foreground-contrast" | "foreground-white" | "foreground-red" |
	"foreground-100" | "foreground-200" | "foreground-300" | "foreground-400" |
	"background-500" | "background-600" | "background-700" | "background-800" | "background-1000" |
	"brand-main" | "brand-main-light" | "brand-main-dark" |
	"tone-red" | "tone-orange" | "tone-yellow" | "tone-green" |
	"tone-indigo" | "tone-purple" | "tone-teal" | "tone-light-blue";

export type ColorState = "default" | "hover" | "active" | "focus";

export type StatefulValue<T> = T | Partial<Record<ColorState, T>>;

/**
 * `responsive`, keyed by interaction state instead of width — same scalar-or-object shape, and the
 * same safelist caveat, since these classes are built at runtime too. `focus` emits
 * `focus-visible:`: the kit styles keyboard focus only, never a mouse click's lingering ring.
 */
export function stateful<T extends string>(
		value: StatefulValue<T> | undefined,
		toClass: (value: T) => string
): string {
	return variantClasses(value, STATE_PREFIX, toClass);
}

// Base first, then states, so a later state beats the base at equal specificity.
const STATE_PREFIX: Record<ColorState, string> = {
	default: "",
	hover: "hover:",
	active: "active:",
	focus: "focus-visible:"
};
